import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchProducts } from '../requests';
import { buildModelUrl } from '../utils/modelUrl';
import Product from '../components/Product';
import BreadcrumbsComponent from '../components/Breadcrumbs';
import Loader from '../components/Loader';
import SpecGrid from '../components/SpecGrid';
import { ProductImage, ManufacturerLogo } from '../components/ProductImage';
import PriceHistoryChart from '../components/PriceHistoryChart';
import { useSelection, selectionKey } from '../context/SelectionContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCarSide, faChartLine } from '@fortawesome/free-solid-svg-icons';
import { formatKwota, seriesFromMatchKey, classifyAvailability } from '../utils';
import useDocumentTitle from '../hooks/useDocumentTitle';
import RelatedArticles from '../components/RelatedArticles';
import MarketStats from '../components/MarketStats';
import MarketFaq from '../components/MarketFaq';
import ProductPanels from '../components/ProductPanels';
import { jestPolska } from '../kraj';
import { buildSpecs } from '../components/specyfikacjaModelu';
import { ZachetyWMarginesach, ZachetyWTresci } from '../components/promo/ZachetyModelu';

// Etykiety kategorii (nadrzędnej i typu) są w słowniku `model`:
// kategoria_nadrzedna.<kategoria> i typ_produktu.<kategoria>.
const PARENT = {
  panel: { to: '/panele' },
  inwerter: { to: '/inwertery' },
  magazyn: { to: '/magazyny' },
  wallbox: { to: '/ladowarki' },
};
const ENDPOINT = {
  panel: 'get_panel_model',
  inwerter: 'get_inverter_model',
  magazyn: 'get_magazine_model',
  wallbox: 'get_wallbox_model',
};


const ModelDetails = () => {
  const { t } = useTranslation('model');
  const { category, slugId } = useParams();
  // ID jest jedynym źródłem prawdy i zawsze jest na końcu parametru — slug
  // (jeśli jest) to czysta dekoracja SEO, nie waliduje się jej treści.
  const idMatch = slugId ? slugId.match(/(\d+)$/) : null;
  const id = idMatch ? idMatch[1] : null;

  const [model, setModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offerSort, setOfferSort] = useState('price'); // 'price' | 'availability'
  const { openHistoryChart, closeHistoryChart, isHistoryChartOpen } = useSelection();

  const valid = ['panel', 'inwerter', 'magazyn', 'wallbox'].includes(category) && id != null;

  useEffect(() => { window.scrollTo(0, 0); }, [id]);

  useEffect(() => {
    if (!valid) { setLoading(false); return; }
    const load = async () => {
      setLoading(true);
      const data = await fetchProducts(`${ENDPOINT[category]}/${id}`);
      setModel(data && !data.error ? data : null);
      setLoading(false);
    };
    load();
  }, [category, id, valid]);

  // Adres z nazwą modelu (2026-10-30): kafelki i odnośniki z ofert znają tylko identyfikator, a poza pierwszym wejściem
  // na stronę nie przechodzą przez przekierowanie z backendu, więc w pasku zostawał sam numer.
  const navigate = useNavigate();
  useEffect(() => {
    if (!model?.slug || String(model.id) !== id) return;
    const adres = buildModelUrl(category, model);
    if (`/model/${category}/${slugId}` !== adres) navigate(adres, { replace: true });
  }, [model, category, slugId, id, navigate]);

  useDocumentTitle(model?.name);

  if (loading) return (<div className='fixed inset-0 flex justify-center items-center'><Loader /></div>);
  if (!valid || !model || model.id == null) {
    return <div className="px-4 lg:px-24 mt-10 text-gray-500">{t('nie_znaleziono')}</div>;
  }

  const offers = model.offers || [];
  // Sortowanie ofert: Wg. Ceny (rosnąco, bez ceny na końcu) | Wg. Dostępności (wg tone
  // z classifyAvailability — "Dostępny" wyżej niż "0 szt.", liczba sztuk jako tie-breaker).
  const TONE_RANK = { high: 5, mid: 4, low: 3, order: 2, unknown: 1, out: 0 };
  const availNum = (a) => { const m = String(a ?? '').match(/\d+/); return m ? parseInt(m[0], 10) : -1; };
  const sortedOffers = [...offers].sort((a, b) => {
    if (offerSort === 'availability') {
      const rankDiff = (TONE_RANK[classifyAvailability(b.availability).tone] ?? 0)
        - (TONE_RANK[classifyAvailability(a.availability).tone] ?? 0);
      return rankDiff !== 0 ? rankDiff : availNum(b.availability) - availNum(a.availability);
    }
    const pa = a.price == null ? Infinity : Number(a.price);
    const pb = b.price == null ? Infinity : Number(b.price);
    return pa - pb;
  });
  const priceMin = formatKwota(model.price_min, 0);
  const wpMin = category === 'panel' ? formatKwota(model.price_wp_min, 2) : null;
  const parent = PARENT[category];
  const specs = buildSpecs(model, category);
  const historyKey = selectionKey(category, model.id);
  const chartOpen = isHistoryChartOpen(historyKey);
  // Nagłówek: Typ · Producent [· Seria, jeśli rozpoznana]. Brak wartości -> pomijana, bez placeholderów.
  // Seria = pole `series` z backendu (backend_api/series.py): scala warianty
  // AM2/SM2/BM3, dokłada klasę napięcia przy Deye i działa dla WSZYSTKICH trzech
  // kategorii, nie tylko falowników (PLAN_SERIE §4).
  //
  // `seriesFromMatchKey` zostaje jako zapas WYŁĄCZNIE dla falowników: u modułów
  // piąty segment `match_key` trzyma długość w mm, więc ten fallback wypisałby
  // w nagłówku „1762" jako serię.
  const series = model.series || (category === 'inwerter' ? seriesFromMatchKey(model.match_key) : null);
  const headerLine = [t(`typ_produktu.${category}`), model.manufacturer || null, series].filter(Boolean).join(' · ');

  // Kompatybilność (falownik↔magazyn) pokazujemy na stronie MODELU:
  // - falowniki tylko hybrydowe (jedyne z portem baterii),
  // - magazyny zawsze (pokazujemy pasujące falowniki hybrydowe).
  const isHybridInverter = category === 'inwerter' && String(model.type || '').trim().toLowerCase() === 'hybrydowy';
  const showCompatibility = isHybridInverter || category === 'magazyn';
  const compatDirection = category === 'magazyn' ? 'battery' : 'inverter';

  return (
    <div className="relative">
      {/* Zachęty dalej (Oferta pod lupą, Asystent doboru): na bardzo szerokim ekranie w marginesach,
          na węższym pod listą ofert. Wejścia z Google i ChatGPT lądują właśnie tutaj (2026-10-08). */}
      <ZachetyWMarginesach kategoria={category} modelId={model.id} />
      <div className="max-w-7xl mx-auto px-4 md:px-8 mt-10">
        <BreadcrumbsComponent parentLabel={t(`kategoria_nadrzedna.${category}`)} parentTo={parent.to} currentLabel={model.name} />
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 mt-6">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 md:p-7">
          <div className="grid md:grid-cols-[300px_1fr] gap-6 lg:gap-8">
            <div className="flex flex-col items-center gap-3">
              <ManufacturerLogo manufacturer={model.manufacturer} productType={category} className="h-8" />
              <ProductImage src={model.img_url} productType={category} alt={model.name} className="max-h-72 w-full object-contain rounded-xl border border-gray-100 bg-white p-3" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide">{headerLine}</p>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900 leading-tight mt-1">{model.name}</h1>
              <div className="mt-4">
                <p className="text-sm text-gray-400 uppercase tracking-wide">{t('cena_od')}</p>
                <div className="flex items-center gap-3">
                  <p className="text-3xl md:text-4xl font-extrabold text-blue-600 leading-none">{priceMin !== null ? priceMin : '—'}</p>
                  {/* Wallboxy nie mają historii cen (brak tabeli i endpointu): bez martwego przycisku. */}
                  {category !== 'wallbox' && <button
                    type="button"
                    onClick={() => openHistoryChart(historyKey)}
                    title={t('historia_ceny')}
                    aria-label={t('pokaz_historie_ceny')}
                    className="inline-flex items-center justify-center h-8 w-8 rounded-lg border border-blue-200 text-blue-600 hover:bg-blue-50 transition-colors shrink-0 text-sm"
                  >
                    <FontAwesomeIcon icon={faChartLine} />
                  </button>}
                </div>
                {wpMin !== null && <p className="text-base font-semibold text-gray-500 mt-1">{t('od_za_wp', { kwota: wpMin })}</p>}
              </div>
              <div className="mt-6 pt-6 border-t border-gray-100">
                <div className="flex items-center gap-2 mb-3">
                  <h2 className="text-sm font-bold text-gray-900">{t('dane_produktu')}</h2>
                  <span className="text-[11px] px-2 py-0.5 rounded-md bg-gray-100 text-gray-500">{t('dane_produktu_zrodlo')}</span>
                </div>
                <SpecGrid items={specs} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Parametry elektryczne + opis produktu w JEDNYM pasku zakładek, maks. jedna
          otwarta naraz. Dane katalogowe producenta to inna warstwa wiarygodności
          niż „Dane produktu" wyżej: tamto jest scalone z ofert sklepów, to pochodzi
          z karty producenta albo listy CEC/PVsyst i ma podane źródło.
          Brak dopasowania i brak opisu = brak całego paska. */}
      <ProductPanels
        spec={model.electrical_spec}
        category={category}
        // Opis składa backend po polsku (z cenami w zł). Wersja krajowa dostanie go,
        // gdy backend będzie umiał inny język; do tego czasu bez opisu.
        paragraphs={jestPolska ? model.description_paragraphs : null}
        compat={showCompatibility ? { direction: compatDirection, modelId: model.id } : null}
        certyfikat={category === 'inwerter' ? model.certyfikat_nc_rfg : null}
      />

      <PriceHistoryChart
        modelId={model.id}
        category={category}
        modelName={model.name}
        open={chartOpen}
        onClose={() => closeHistoryChart(historyKey)}
      />

      <MarketStats marketStats={model.market_stats} category={category} />


      <div className="max-w-5xl mx-auto px-4 md:px-8 mt-8 mb-8">
        <div className="flex items-center justify-between gap-3 mb-4">
          <h2 className="text-lg font-bold text-blue-600">{t('oferty')} <span className="text-blue-400 font-normal">({offers.length})</span></h2>
          {offers.length > 1 && (
            <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden text-sm shrink-0">
              <button
                type="button"
                onClick={() => setOfferSort('price')}
                className={`px-3 py-1.5 font-medium transition-colors ${offerSort === 'price' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                {t('sortuj_cena')}
              </button>
              <button
                type="button"
                onClick={() => setOfferSort('availability')}
                className={`px-3 py-1.5 font-medium border-l border-gray-200 transition-colors ${offerSort === 'availability' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                {t('sortuj_dostepnosc')}
              </button>
            </div>
          )}
        </div>
        {offers.length === 0 ? (<p className="text-sm text-gray-400">{t('brak_ofert')}</p>) : (
          <ul>{sortedOffers.map((o) => (<Product key={o.id} product={o} productType={category} compact />))}</ul>
        )}
      </div>

      <div className="max-w-5xl mx-auto px-4 md:px-8 mb-8">
        <ZachetyWTresci kategoria={category} modelId={model.id} />
        {/* Do 2026-10 karta wallboxa nie prowadziła do doboru ładowarki (raport ładowarek EV). */}
        {category === 'wallbox' && jestPolska && (
          <Link to="/dobor-ladowarki" className="inline-flex items-center gap-2 font-semibold text-blue-600 hover:underline">
            <FontAwesomeIcon icon={faCarSide} className="h-4 w-4" />
            Czy ta moc pasuje do Twojego auta? Sprawdź w doborze ładowarki
          </Link>
        )}
      </div>

      <MarketFaq marketStats={model.market_stats} modelName={model.name} category={category} />

      {/* Na samym dole: najpierw produkt i oferty, potem pytania, na koniec
          odeslanie do Bazy wiedzy — naturalna kolejnosc czytania. */}
      {/* Baza wiedzy jest tylko po polsku: w wersji krajowej bez odsyłaczy. */}
      {jestPolska && <RelatedArticles articles={model.related_articles} />}
    </div>
  );
};

export default ModelDetails;
